import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, User, Zap, PenTool, Hand, Droplets } from "lucide-react";
import MainLayout from "../../components/layout/MainLayout";
import { supabase } from "../../lib/supabase";

export default function ConsulenzaPage() {

  const [step, setStep] = useState(1);
  const [zona, setZona] = useState("");
  const [obiettivo, setObiettivo] = useState("");
  const [skinType, setSkinType] = useState("");
  const [trattamenti, setTrattamenti] = useState<any[]>([]);

  const navigate = useNavigate();
  const progress = (step / 3) * 100;

  const categorie = [
    { id: "latest", label: "Latest", icon: Sparkles },
    { id: "viso", label: "Viso", icon: User },
    { id: "corpo", label: "Corpo", icon: Sparkles },
    { id: "laser", label: "Laser", icon: Zap },
    { id: "pmu", label: "PMU", icon: PenTool },
    { id: "nails", label: "Nails", icon: Hand },
    { id: "cococera", label: "Cococera", icon: Droplets }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase.from("trattamenti").select("*").order("created_at", { ascending: false });
      if (data) setTrattamenti(data);
    };

    fetchData();
  }, []);

  const trattamentiFiltrati = trattamenti
    .filter((t) => (zona ? t.categoria === zona || t.categoria === "latest" : true))
    .filter((t) => {
      if (!obiettivo) return true;
      const m = (t.descrizione_breve || "" + t.descrizione || "").toLowerCase();
      if (obiettivo === "migliorare") return m.includes("miglior") || m.includes("glow") || m.includes("idrata");
      if (obiettivo === "ridurre") return m.includes("imperfezioni") || m.includes("macchie") || m.includes("rossore");
      if (obiettivo === "tonificare") return m.includes("tonic") || m.includes("sodi") || m.includes("elasticit");
      return true;
    })
    .filter((t) => {
      if (!skinType || skinType === "normale") return true;
      const txt = (t.descrizione_breve || "" + t.descrizione || "").toLowerCase();
      if (skinType === "secca") return txt.includes("secca") || txt.includes("idrat") || txt.includes("arido");
      if (skinType === "mista") return txt.includes("mista") || txt.includes("zona t") || txt.includes("equilibrio");
      if (skinType === "grassa") return txt.includes("grassa") || txt.includes("oleos") || txt.includes("lucid");
      if (skinType === "sensibile") return txt.includes("sensibile") || txt.includes("delic") || txt.includes("rossor");
      return true;
    });

  return (
    <MainLayout>

      <section className="py-24 px-6 text-center">

        <h1 className="text-4xl font-serif mb-4">
          Analizza la tua pelle
        </h1>

        <p className="text-gray-600 mb-10">
          Scopri il trattamento più adatto in pochi secondi
        </p>

        {/* PROGRESS BAR */}
        <div className="max-w-md mx-auto mb-16">
          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div
              className="bg-black h-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* STEP 1 */}
        {step === 1 && (

          <div className="space-y-8">

            <p className="text-lg">Scegli la categoria di trattamento più vicina al tuo bisogno</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categorie.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setZona(cat.id === "latest" ? "" : cat.id);
                    setStep(2);
                  }}
                  className="px-3 py-4 rounded-xl border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:shadow-lg hover:border-black transition"
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-500">Puoi sempre cambiare categoria dopo la consulenza.</p>

          </div>

        )}

        {/* STEP 2 */}
        {step === 2 && (

          <div className="space-y-8">

            <p className="text-lg">Qual è il tuo obiettivo principale?</p>

            <div className="flex flex-wrap justify-center gap-4">

              <button
                onClick={() => setObiettivo("migliorare")}
                className={`px-6 py-3 border rounded-full ${obiettivo === "migliorare" ? "bg-black text-white" : "hover:bg-gray-100"}`}
              >
                Migliorare la pelle
              </button>

              <button
                onClick={() => setObiettivo("ridurre")}
                className={`px-6 py-3 border rounded-full ${obiettivo === "ridurre" ? "bg-black text-white" : "hover:bg-gray-100"}`}
              >
                Ridurre imperfezioni
              </button>

              <button
                onClick={() => setObiettivo("tonificare")}
                className={`px-6 py-3 border rounded-full ${obiettivo === "tonificare" ? "bg-black text-white" : "hover:bg-gray-100"}`}
              >
                Tonificare
              </button>

            </div>

            <p className="text-lg">Che tipo di pelle hai?</p>

            <div className="flex flex-wrap justify-center gap-4">
              {[
                { value: "normale", label: "Normale" },
                { value: "secca", label: "Secca" },
                { value: "mista", label: "Mista" },
                { value: "grassa", label: "Grassa" },
                { value: "sensibile", label: "Sensibile" },
              ].map((item) => (
                <button
                  key={item.value}
                  onClick={() => setSkinType(item.value)}
                  className={`px-6 py-3 border rounded-full ${skinType === item.value ? "bg-black text-white" : "hover:bg-gray-100"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                if (!obiettivo || !skinType) return;
                setStep(3);
              }}
              className="mt-4 px-10 py-3 bg-black text-white rounded-full hover:scale-105 transition"
            >
              Vai ai consigli
            </button>

          </div>

        )}

        {/* STEP 3 — RISULTATO */}
        {step === 3 && (

          <div className="space-y-12 max-w-2xl mx-auto">

            <div className="flex justify-center">
              <span className="
                px-4 py-1.5
                text-xs
                tracking-widest
                uppercase
                rounded-full
                bg-black
                text-white
              ">
                Consulenza completata
              </span>
            </div>

            <h2 className="text-3xl font-serif">
              Il prossimo passo è una consulenza personalizzata
            </h2>

            <p className="text-gray-600 leading-relaxed">
              Ogni pelle è diversa. Per offrirti il miglior risultato,
              ti consigliamo una consulenza diretta con le nostre specialiste.
            </p>

            <p className="text-gray-500 text-sm">
              Categoria scelta: <span className="font-semibold">{zona || "tutti"}</span>
            </p>
            <p className="text-gray-500 text-sm">
              Obiettivo: <span className="font-semibold">{obiettivo || "non specificato"}</span>
            </p>
            <p className="text-gray-500 text-sm">
              Tipo di pelle: <span className="font-semibold">{skinType || "non specificato"}</span>
            </p>

            <a
              href="https://wa.me/390683796865"
              target="_blank"
              className="
                inline-block
                px-10 py-4
                bg-black text-white
                rounded-full
                hover:scale-105
                transition
              "
            >
              Prenota su WhatsApp
            </a>

            <div className="mt-10">
              <h3 className="text-2xl font-serif mb-5">Trattamenti consigliati</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
                {categorie.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => navigate(`/trattamenti?categoria=${cat.id}`)}
                      className="p-3 rounded-2xl bg-white shadow-sm border border-black/10 text-left hover:scale-[1.02] transition"
                    >
                      <div className="inline-flex items-center justify-center mb-2 w-8 h-8 rounded-full bg-black/10 text-black">
                        <Icon className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">{cat.label}</p>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-3">
                {trattamentiFiltrati.length === 0 && (
                  <p className="text-sm text-gray-500">Nessun trattamento trovato per la selezione scelta.</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {trattamentiFiltrati.slice(0, 6).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => navigate(`/trattamenti/${t.id}`)}
                      className="text-left p-4 border border-black/10 rounded-2xl bg-white hover:shadow-md transition"
                    >
                      <p className="font-semibold">{t.nome}</p>
                      <p className="text-xs text-gray-500">{t.categoria} • {t.durata}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>

        )}

      </section>

    </MainLayout>
  );
}